import { ApiError } from "./types";
import { captureApiException, newRequestId } from "@/shared/lib/sentry";

type ApiOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
};

/**
 * Report server faults (5xx) and network/fetch failures to Sentry. Expected 4xx
 * (auth, validation, not-found) are deliberately NOT reported — they're routine
 * client outcomes, not incidents. Deduped so react-query's central onError
 * doesn't double-count the same error object.
 */
function reportApiFailure(
  error: unknown,
  meta: { route: string; status: number; requestId: string },
): void {
  captureApiException(error, {
    tags: {
      route: meta.route,
      status: meta.status,
      request_id: meta.requestId,
      component: "api",
    },
  });
}

/**
 * FastAPI's `detail` is only a string by convention. Routes that need to return
 * structured context raise `HTTPException(409, {message, reasons})`, and passing
 * that object straight to `new Error()` renders as "[object Object]" — which is
 * what a user saw when a production day was not lockable. Take the object's
 * `message`, and append the reason list when the backend supplied one.
 */
function detailToMessage(detail: unknown): string | undefined {
  if (typeof detail === "string") return detail;
  if (!detail) return undefined;

  // FastAPI's OWN validation errors — every 422 the API emits — are an ARRAY:
  //   [{ loc: ["body","priority"], msg: "unexpected value", type: "value_error" }]
  // This branch did not exist, so the array fell through to the object handling below,
  // produced undefined, and the user was shown `statusText` instead. Against the
  // deployed service that is HTTP/2, where `statusText` is ALWAYS the empty string —
  // so a 422 rendered as a toast containing nothing at all.
  //
  // `loc` starts with "body"/"query"; dropping that leaves the field the user touched,
  // which is the only part they can act on.
  if (Array.isArray(detail)) {
    const parts = detail
      .map((entry) => {
        if (typeof entry === "string") return entry;
        if (!entry || typeof entry !== "object") return undefined;
        const e = entry as { loc?: unknown; msg?: unknown };
        const msg = typeof e.msg === "string" ? e.msg : undefined;
        const field = Array.isArray(e.loc)
          ? e.loc.slice(1).map((p) => String(p)).join(".")
          : undefined;
        if (field && msg) return `${field}: ${msg}`;
        return msg ?? field;
      })
      .filter((x): x is string => Boolean(x));
    return parts.length ? parts.join("; ") : undefined;
  }

  if (typeof detail !== "object") return undefined;
  const record = detail as Record<string, unknown>;
  const message = typeof record.message === "string" ? record.message : undefined;
  // `reasons` and `blockers` are the same idea under two names — the replenishment
  // routes raise `{message, blockers}` (readiness and approval gates), the production
  // day raises `{message, reasons}`. Both were being dropped on the `blockers` side,
  // so "Program is not ready" reached the user with none of the eight things that
  // would make it ready.
  const parts = [...detailList(record.reasons), ...detailList(record.blockers)];
  if (message && parts.length) return `${message}: ${parts.join(", ")}`;
  return message ?? (parts.length ? parts.join(", ") : undefined);
}

/** A `reasons`/`blockers` payload as a list of strings, ignoring anything else. */
function detailList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => String(entry)).filter(Boolean);
}

/** The blocker sentences from a `{message, blockers}` body, for callers that
 *  render them individually rather than as the flattened message. */
export function detailToBlockers(detail: unknown): string[] | undefined {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return undefined;
  const list = detailList((detail as Record<string, unknown>).blockers);
  return list.length ? list : undefined;
}

/** Test seam. The parser is the part that broke; exporting it keeps the test honest
 *  without having to stand up a fetch mock for every shape. */
export const detailToMessageForTest = detailToMessage;

/** Parse the backend error envelope into a typed ApiError (with request id + code). */
function toApiError(
  status: number,
  body: { detail?: unknown; message?: string; code?: string; request_id?: string; error?: { code?: string; request_id?: string } },
  statusText: string,
  fallbackRequestId: string,
): ApiError {
  const requestId = body.request_id ?? body.error?.request_id ?? fallbackRequestId;
  const code = body.code ?? body.error?.code;
  // NEVER fall back to a bare `statusText`. The deployed services negotiate HTTP/2,
  // where statusText is always "" — so every unparsed error produced an ApiError whose
  // message was the empty string, and the user got a toast with no text in it.
  const message = detailToMessage(body.detail) ?? body.message ?? (statusText || `HTTP ${status}`);
  return new ApiError(status, message, requestId, code, detailToBlockers(body.detail), body.detail);
}

/**
 * In-memory session token, used as the X-Session-Token header on every request.
 *
 * SEEDED FROM localStorage AT MODULE LOAD, which matters more than it looks.
 * AuthContext calls setSessionToken() from an effect, so on a cold load there is
 * a window where components have mounted and their queries have fired but the
 * token is still null. Those requests go out unauthenticated, get a 401, and the
 * global retry policy correctly refuses to retry a 4xx — so the page stays on an
 * error state until a manual reload. Intermittent by nature: it only loses when a
 * query beats the effect, which depends on load order and cache warmth.
 *
 * The symptom was "Supplier Update API is unavailable" appearing at random on
 * staging. Five call sites had already worked around it locally with
 * `getSessionToken() ?? localStorage.getItem(...)` — but only on paths that use
 * raw fetch (uploads, downloads). Everything going through `apiRequest`, which is
 * almost every read in the app, had no fallback at all.
 *
 * Strictly additive: this only supplies a token where there was none, so it
 * cannot change the behaviour of an already-authenticated request. A stale token
 * 401s exactly as a missing one does, and AuthContext clears the key when the
 * session turns out to be invalid.
 */
let _sessionToken: string | null = (() => {
  try {
    return localStorage.getItem("ubik_session_token");
  } catch {
    // Blocked-cookie / private-mode contexts throw on access.
    return null;
  }
})();

export function setSessionToken(token: string | null) {
  _sessionToken = token;
}

export function getSessionToken(): string | null {
  return _sessionToken;
}

/**
 * Origin for browser WebSocket connections (live meeting transcript / lifecycle).
 *
 * Uses VITE_WS_BASE_URL (the backend, e.g. wss://ubik.solarpunk.technology) when
 * set, normalized to a ws(s):// origin. This is load-bearing: the backend serves
 * /ws/session, /ws/user and /ws/meeting at ROOT (no /api/v1 prefix), and the
 * staging/prod web-origin proxy forwards /api/* but NOT /ws/*. So building the
 * socket from window.location.host routes it at the web origin, where the proxy
 * silently drops it — killing every live push (status flip on SESSION_ENDED,
 * live transcript, intelligence). Going straight to the backend (like the
 * desktop app, and like REST via VITE_API_BASE_URL) bypasses the proxy; the
 * ?session= token authenticates the socket cross-origin (no cookie needed).
 *
 * Falls back to the current origin for local dev, where the Vite dev server
 * proxies /ws/* to staging and no explicit WS base is set.
 */
/**
 * Absolute origin for REST calls. Empty on the web (relative URLs, same-origin
 * behind nginx — byte-for-byte today's behaviour). Set VITE_API_ORIGIN only for
 * builds whose page origin is not the API edge: the Capacitor app runs on
 * https://localhost, so it builds with VITE_API_ORIGIN=https://theubik.com.
 */
export const API_ORIGIN = ((import.meta.env.VITE_API_ORIGIN as string | undefined) ?? "").replace(/\/+$/, "");

/**
 * Expose the resolved origin so the e2e harness can verify what it is talking
 * to. This is the load-bearing safety property of the screenshot suite, and it
 * was previously unobservable: `VITE_USE_MOCK_API` reads as the safety switch
 * but defaults to mock anyway (`!== "false"` below), and `USE_MOCK` is only a
 * per-endpoint convention that roughly half the modules deliberately opt out
 * of. What actually keeps traffic off real backends is this string being
 * empty, which makes every request relative to the page origin.
 *
 * Also doubles as a build marker: `playwright.config.ts` sets
 * `reuseExistingServer`, so a hand-started `vite preview` on a stale dist can
 * be adopted silently. If this global is missing, the suite is not running the
 * build it thinks it is.
 */
if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).__UBIK_API_ORIGIN__ = API_ORIGIN;
}

/** Prefix a backend path ("/v1/...") with the API origin + /api. */
export const apiUrl = (path: string): string => `${API_ORIGIN}/api${path}`;

export function wsOrigin(): string {
  const base = import.meta.env.VITE_WS_BASE_URL as string | undefined;
  if (base) return base.replace(/^http/, "ws").replace(/\/+$/, "");
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}`;
}

async function apiRequest<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const requestId = newRequestId();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Request-ID": requestId,
  };

  if (_sessionToken) {
    headers["X-Session-Token"] = _sessionToken;
  }

  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method: options.method ?? "GET",
      headers,
      credentials: "include",
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (err) {
    // Network/fetch failure (offline, DNS, TLS) — report unless the caller aborted.
    if ((err as Error).name !== "AbortError") {
      reportApiFailure(err, { route: path, status: 0, requestId });
    }
    throw err;
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    if (res.status === 403 && error?.error?.code === "zoho_not_connected") {
      window.location.assign("/integrations");
      return undefined as T;
    }
    const apiError = toApiError(res.status, error, res.statusText, requestId);
    if (res.status >= 500) {
      reportApiFailure(apiError, { route: path, status: res.status, requestId: apiError.requestId ?? requestId });
    }
    throw apiError;
  }

  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

/**
 * Upload a single file via multipart/form-data.
 * Cannot use apiRequest because Content-Type must be set by the browser.
 */
export async function uploadFile<T>(path: string, file: File): Promise<T> {
  const requestId = newRequestId();
  const headers: Record<string, string> = { "X-Request-ID": requestId };
  if (_sessionToken) headers["X-Session-Token"] = _sessionToken;

  const formData = new FormData();
  formData.append("file", file);

  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method: "POST",
      headers,
      credentials: "include",
      body: formData,
    });
  } catch (err) {
    if ((err as Error).name !== "AbortError") {
      reportApiFailure(err, { route: path, status: 0, requestId });
    }
    throw err;
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    const apiError = toApiError(res.status, error, res.statusText, requestId);
    if (res.status >= 500) {
      reportApiFailure(apiError, { route: path, status: res.status, requestId: apiError.requestId ?? requestId });
    }
    throw apiError;
  }

  return res.json() as Promise<T>;
}

/**
 * POST that returns a binary body, for server-rendered downloads.
 *
 * Cannot use apiRequest, which assumes JSON. Cannot be a plain anchor either:
 * auth travels in the X-Session-Token header, so a link would download an HTML
 * 401 page named like a PDF.
 */
export async function downloadBlob(
  path: string,
  body: unknown,
): Promise<{ blob: Blob; filename: string | null }> {
  const requestId = newRequestId();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Request-ID": requestId,
  };
  if (_sessionToken) headers["X-Session-Token"] = _sessionToken;

  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(body),
    });
  } catch (err) {
    if ((err as Error).name !== "AbortError") {
      reportApiFailure(err, { route: path, status: 0, requestId });
    }
    throw err;
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    const apiError = toApiError(res.status, error, res.statusText, requestId);
    if (res.status >= 500) {
      reportApiFailure(apiError, { route: path, status: res.status, requestId: apiError.requestId ?? requestId });
    }
    throw apiError;
  }

  // Prefer the server's filename — it knows the memo's period key.
  const disposition = res.headers.get("content-disposition") ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return { blob: await res.blob(), filename: match ? match[1] : null };
}

/**
 * GET that returns a binary body, for an authenticated image or file.
 *
 * The GET twin of `downloadBlob`, for the same reason: auth travels in the
 * X-Session-Token header, so a bare `<img src>` would load a 401. Render the
 * result through `URL.createObjectURL` and revoke it when it leaves the screen.
 */
export async function getBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const requestId = newRequestId();
  const headers: Record<string, string> = { "X-Request-ID": requestId };
  if (_sessionToken) headers["X-Session-Token"] = _sessionToken;

  let res: Response;
  try {
    res = await fetch(apiUrl(path), { method: "GET", headers, credentials: "include", signal });
  } catch (err) {
    if ((err as Error).name !== "AbortError") {
      reportApiFailure(err, { route: path, status: 0, requestId });
    }
    throw err;
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    const apiError = toApiError(res.status, error, res.statusText, requestId);
    if (res.status >= 500) {
      reportApiFailure(apiError, { route: path, status: res.status, requestId: apiError.requestId ?? requestId });
    }
    throw apiError;
  }

  return res.blob();
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    apiRequest<T>(path, { signal }),
  post: <T>(path: string, body: unknown, opts?: { signal?: AbortSignal }) =>
    apiRequest<T>(path, { method: "POST", body, signal: opts?.signal }),
  patch: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, { method: "PATCH", body }),
  put: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, { method: "PUT", body }),
  delete: <T>(path: string) =>
    apiRequest<T>(path, { method: "DELETE" }),
};

const USE_MOCK = import.meta.env.VITE_USE_MOCK_API !== "false";

export function delay(ms = 250): Promise<void> {
  if (!USE_MOCK) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Stream Server-Sent Events from a POST endpoint.
 * Uses fetch + ReadableStream instead of EventSource so we can send
 * custom headers (X-Session-Token).
 *
 * Each SSE line arrives as a raw `data: <JSON>` string.
 * `onEvent` is called once per event with just the JSON string (prefix stripped).
 */
export async function streamSSE(
  path: string,
  body: unknown,
  onEvent: (json: string) => void,
  signal: AbortSignal,
): Promise<void> {
  const requestId = newRequestId();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    "X-Request-ID": requestId,
  };

  if (_sessionToken) {
    headers["X-Session-Token"] = _sessionToken;
  }

  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") return;
    reportApiFailure(err, { route: path, status: 0, requestId });
    throw err;
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    const apiError = toApiError(res.status, error, res.statusText, requestId);
    if (res.status >= 500) {
      reportApiFailure(apiError, { route: path, status: res.status, requestId: apiError.requestId ?? requestId });
    }
    throw apiError;
  }

  const reader = res.body?.getReader();
  if (!reader) return;

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Split on double-newline (SSE event boundary)
      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";

      for (const part of parts) {
        for (const line of part.split("\n")) {
          if (line.startsWith("data:")) {
            const json = line.slice(5).trim();
            if (json && json !== "[DONE]") {
              onEvent(json);
            }
          }
        }
      }
    }
  } catch (err) {
    if ((err as Error).name === "AbortError") return;
    throw err;
  } finally {
    reader.releaseLock();
  }
}

export { USE_MOCK };
